import { FunctionComponent, JSX, SyntheticEvent, useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import moment from 'moment-timezone'
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { Daycare } from '@/types/Daycare'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import styles from './editOrCreateWorkDialog.module.scss'
import { ClosingDay } from '@/types/ClosingDays'
import { Work, WorkType } from '@/types/work'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import { CREATE_MULTIPLE_WORK, UPDATE_INTERNAL_WORK } from '@/modules/API/work/internalWork'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import DatePickerComponent from '@/components/_custom/Picker/datePicker/datePicker'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import {
    Autocomplete,
    AutocompleteRenderGetTagProps,
    AutocompleteRenderInputParams,
    Chip,
    TextField
} from '@mui/material'
import { getDaycareClosing, getDaycareDaysSelectOptions, getDaycareOpening } from '@/modules/helper/daycareHelper'
import TextArea from '@/components/_custom/TextArea/textArea'
import { StaffWithLinks } from '@/types/Staff'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { useWeekPattern, UseWeekPatternReturn } from '../../regularSlot/weekPatternSection/useWeekPattern'
import WeekPatternSection from '../../regularSlot/weekPatternSection/WeekPatternSection'
import Title from '@/components/_custom/Title/title.tsx'
import { useAuth } from '@/modules/context/AuthContext.tsx'
import DayTimeRow from './dayTimeRow'
import WorkPreview from './workPreview'
import SelectMultipleDateDialogComponent from '../../occasionalSlot/occasionalSlotForm/components/SelectMultipleDateDialog/selectMultipleDateDialog'
import { useDialog } from '@/hooks/useDialog/useDialog'

interface EditOrCreateWorkDialogProps {
    open: boolean
    handleClose: () => void
    work?: Work | undefined
    daycare?: Daycare
    staffsListData: Array<StaffWithLinks>
    closingDays?: Array<ClosingDay>
    date: string
    from: string
    to: string
    works: Array<Work>
    dateReadonly?: boolean
    refetch: () => void
    closeParentDialog?: () => void
    selectedStaffId?: string
}

const EditOrCreateWorkDialog: FunctionComponent<EditOrCreateWorkDialogProps> = ({
    open,
    work,
    daycare,
    closingDays,
    date,
    from,
    to,
    works,
    staffsListData,
    handleClose,
    dateReadonly = true,
    refetch,
    closeParentDialog,
    selectedStaffId
}: EditOrCreateWorkDialogProps) => {
    if (!daycare) return
    const { t } = useTranslation()
    const { showNotificationSuccess, showNotificationError } = useNotification()
    const { user } = useAuth()
    const canUpdatePreparationTasks: boolean = !!user?.currentDaycareRights.canWriteParamsDaycare

    const [previewDay, setPreviewDay] = useState<string | null>(null)

    const [staff, setStaff] = useState<string>(work ? work.staff.toString() : selectedStaffId || '')
    const [sectionGroup, setSectionGroup] = useState<string>(work?.sectionGroupId?.toString() || '')
    const [startDate, setStartDate] = useState<string>(date)
    const [endDate, setEndDate] = useState<string>(date)
    const [startHour, setStartHour] = useState<string>(from)
    const [endHour, setEndHour] = useState<string>(to)

    const [hasPreparation, setHasPreparation] = useState<boolean>(
        !!work?.preparationTasksBefore?.length || !!work?.preparationTasksAfter?.length
    )

    const [preparationTasksBefore, setPreparationTasksBefore] = useState<string[]>(
        work?.preparationTasksBefore?.map((t) => t.id.toString()) || []
    )
    const [preparationTasksAfter, setPreparationTasksAfter] = useState<string[]>(
        work?.preparationTasksAfter?.map((t) => t.id.toString()) || []
    )
    const [dayPreparationTasks, setDayPreparationTasks] = useState<
        Record<string, { before: string[]; after: string[] }>
    >({})

    // Never loaded from `work`: a pause is purely input-side and never persisted as such (the
    // backend splits the work into two rows and forgets the pause ever existed), so there's
    // nothing to reconstruct when editing an existing work.
    const [hasPause, setHasPause] = useState<boolean>(false)
    const [pauseStart, setPauseStart] = useState<string>('')
    const [pauseEnd, setPauseEnd] = useState<string>('')
    const [dayPause, setDayPause] = useState<Record<string, { hasPause: boolean; start: string; end: string }>>({})

    const [workType, setWorkType] = useState<string>(work ? work.type.toString() : WorkType.inSection)

    const showPreparationFields: boolean = hasPreparation && workType !== WorkType.holiday

    const [internalNoteValue, setInternalNoteValue] = useState<string>(work?.internalNote || '')
    const [externalNoteValue, setExternalNoteValue] = useState<string>(work?.externalNote || '')

    const [initialWorkValues, setInitialWorkValues] = useState<WorkSnapshot | null>(work ? getWorkSnapshot(work) : null)

    const [chooseDate, setChooseDate] = useState<boolean>(false)
    const [dateList, setDateList] = useState<string[]>([])
    const [selectPeriod, setSelectPeriod] = useState<boolean>(false)
    const [chooseDays, setChooseDays] = useState<boolean>(false)
    const [selectedDays, setSelectedDays] = useState<string[]>([])
    const weekPattern: UseWeekPatternReturn = useWeekPattern()

    const [dayTimes, setDayTimes] = useState<Record<string, { start: string; end: string }>>({})

    const [showMoreOptions, setShowMoreOptions] = useState<boolean>(!selectedStaffId && !work)

    const isCreate: boolean = !work
    const daysMode: boolean =
        (isCreate && (chooseDays || weekPattern.chooseWeekPattern)) || (!isCreate && selectPeriod && chooseDays)
    const isBulkEditMode: boolean = !isCreate && (chooseDate || selectPeriod)
    const dayOfWeek: number = (moment(startDate, 'DD-MM-YYYY').day() + 6) % 7
    const selectDays: SelectOptionsDeprecated = daycare ? getDaycareDaysSelectOptions(daycare, t) : [{ options: [] }]

    const minHour: number = daycare ? Number(moment(getDaycareOpening(daycare, dayOfWeek), 'HH:mm').format('HH')) : 8
    const minMinute: number = daycare ? Number(moment(getDaycareOpening(daycare, dayOfWeek), 'HH:mm').format('mm')) : 0
    const maxHour: number = daycare ? Number(moment(getDaycareClosing(daycare, dayOfWeek), 'HH:mm').format('HH')) : 18
    const maxMinute: number = daycare ? Number(moment(getDaycareClosing(daycare, dayOfWeek), 'HH:mm').format('mm')) : 0
    const range: number = daycare && daycare.merger ? Number(daycare.bookRange.split('_')[1] || '15') : 15

    const activePreviewDay: string | null =
        previewDay === null
            ? null
            : daysMode
              ? selectedDays.includes(previewDay)
                  ? previewDay
                  : null
              : previewDay === 'single'
                ? previewDay
                : null
    const previewTimes: { start: string; end: string } =
        activePreviewDay && daysMode
            ? dayTimes[activePreviewDay] || { start: startHour, end: endHour }
            : { start: startHour, end: endHour }
    const previewPause: { hasPause: boolean; start: string; end: string } | undefined =
        activePreviewDay && daysMode ? dayPause[activePreviewDay] : { hasPause, start: pauseStart, end: pauseEnd }
    const previewPreparation: { before: string[]; after: string[] } | undefined =
        activePreviewDay && daysMode
            ? dayPreparationTasks[activePreviewDay]
            : { before: preparationTasksBefore, after: preparationTasksAfter }
    const previewDayOfWeek: number = activePreviewDay && daysMode ? Number(activePreviewDay) : dayOfWeek

    useEffect(() => {
        setPreviewDay(null)
    }, [open, daysMode])

    const noClosingDay: ClosingDay[] =
        closingDays?.filter((closingDay: ClosingDay) => {
            return (
                moment(closingDay.startDate, 'DD-MM-YYYY').toDate() <= moment(startDate, 'DD-MM-YYYY').toDate() &&
                moment(closingDay.endDate, 'DD-MM-YYYY').toDate() >= moment(startDate, 'DD-MM-YYYY').toDate()
            )
        }) || []

    useEffect((): void => {
        if (work) {
            setStaff(work.staff.toString())
            setSectionGroup(work.sectionGroupId?.toString() || '')
            setStartDate(moment(work.date, 'DD-MM-YYYY').format('DD-MM-YYYY'))
            setEndDate(moment(work.date, 'DD-MM-YYYY').format('DD-MM-YYYY'))
            setStartHour(moment(work.start, 'HH:mm').format('HH:mm'))
            setEndHour(moment(work.end, 'HH:mm').format('HH:mm'))
            setInternalNoteValue(work.internalNote || '')
            setExternalNoteValue(work.externalNote || '')
            setInitialWorkValues(getWorkSnapshot(work))
            setPreparationTasksBefore(work?.preparationTasksBefore?.map((t) => t.id.toString()) || [])
            setPreparationTasksAfter(work?.preparationTasksAfter?.map((t) => t.id.toString()) || [])
            setHasPreparation(!!work.preparationTasksBefore?.length || !!work.preparationTasksAfter?.length)
            setHasPause(false)
            setPauseStart('')
            setPauseEnd('')
            setChooseDate(false)
            setDateList([])
            setSelectPeriod(false)
            setChooseDays(false)
            setSelectedDays([])
        } else {
            setStaff(selectedStaffId ?? '')
            setSectionGroup(daycare?.sectionsGroups?.length === 1 ? daycare?.sectionsGroups?.[0]?.id?.toString() : '')
            setStartDate(date)
            setEndDate(date)
            setStartHour(from)
            setEndHour(to)
            setInternalNoteValue('')
            setExternalNoteValue('')
            setChooseDate(false)
            setDateList([])
            setSelectPeriod(false)
            setChooseDays(false)
            setSelectedDays([])
            weekPattern.setChooseWeekPattern(false)
            setInitialWorkValues(null)
            setPreparationTasksBefore([])
            setPreparationTasksAfter([])
            setHasPreparation(false)
            setHasPause(false)
            setPauseStart('')
            setPauseEnd('')
        }
    }, [work])

    useEffect(() => {
        if (endHour.length === 5 && startHour.length === 5) {
            if (endHour < startHour) setEndHour(startHour)
            if (startHour > endHour) setStartHour(endHour)
        }
    }, [startHour, endHour])

    useEffect(() => {
        setDayTimes((prev) => {
            const updated: Record<string, { start: string; end: string }> = {}
            for (const day of selectedDays) {
                updated[day] = prev[day] || { start: startHour, end: endHour }
            }
            return updated
        })

        setDayPreparationTasks((prev) => {
            const updated: Record<string, { before: string[]; after: string[] }> = {}
            for (const day of selectedDays) {
                updated[day] = prev[day] || { before: preparationTasksBefore, after: preparationTasksAfter }
            }
            return updated
        })

        setDayPause((prev) => {
            const updated: Record<string, { hasPause: boolean; start: string; end: string }> = {}
            for (const day of selectedDays) {
                updated[day] = prev[day] || { hasPause, start: pauseStart, end: pauseEnd }
            }
            return updated
        })
    }, [selectedDays])

    function updateDayTime(day: string, field: 'start' | 'end', value: string): void {
        setDayTimes((prev) => ({
            ...prev,
            [day]: { ...prev[day], [field]: value }
        }))
    }

    function updateDayPause(day: string, field: 'hasPause' | 'start' | 'end', value: boolean | string): void {
        setDayPause((prev) => ({
            ...prev,
            [day]: { ...(prev[day] || { hasPause: false, start: '', end: '' }), [field]: value }
        }))
    }

    function updateDayPreparationTasks(day: string, field: 'before' | 'after', value: string[]): void {
        setDayPreparationTasks((prev) => ({
            ...prev,
            [day]: { ...prev[day], [field]: value }
        }))
    }

    function getDayLabel(day: string): string {
        return selectDays[0]?.options.find((o) => o.value === day)?.name ?? day
    }

    function handleCheckChooseDate(checked: boolean): void {
        if (checked) openMultipleDateDialog()
        setChooseDate(checked)
        if (checked) {
            setChooseDays(false)
            setSelectedDays([])
            weekPattern.setChooseWeekPattern(false)
            setSelectPeriod(false)
        }
    }

    function handleCheckSelectPeriod(checked: boolean): void {
        setSelectPeriod(checked)
        if (checked) {
            setChooseDate(false)
            setDateList([])
        } else {
            setChooseDays(false)
            setSelectedDays([])
        }
    }

    function handleCheckPreparation(): void {
        setHasPreparation(!hasPreparation)
    }

    function handleCheckChooseDays(checked: boolean): void {
        setChooseDays(checked)
        if (checked) {
            setChooseDate(false)
            weekPattern.setChooseWeekPattern(false)
        }
    }

    function handleCheckChooseWeekPattern(checked: boolean): void {
        weekPattern.setChooseWeekPattern(checked)
        if (checked) {
            setChooseDate(false)
            setChooseDays(false)
        }
    }

    function handleDateRemove(dateToRemove: string): void {
        setDateList(dateList.filter((d: string) => d !== dateToRemove))
    }

    const autocompleteStaff: SelectOptionsDeprecated = [
        {
            options:
                staffsListData && staffsListData.length > 0
                    ? staffsListData
                          .filter((staffLink) => {
                              return work && work.staff === staffLink.id
                                  ? true
                                  : works && works.length > 0
                                    ? works.filter((workFilter) => {
                                          return (
                                              workFilter.id !== work?.id &&
                                              workFilter.staff === staffLink.id &&
                                              moment(workFilter.date, 'DD-MM-YYYY').format('DD-MM-YYYY') ===
                                                  startDate &&
                                              moment(workFilter.end, 'HH:mm').format('HH:mm') >
                                                  moment(startHour, 'HH:mm').format('HH:mm') &&
                                              moment(workFilter.start, 'HH:mm').format('HH:mm') <
                                                  moment(endHour, 'HH:mm').format('HH:mm')
                                          )
                                      }).length === 0
                                    : true
                          })
                          .map((s) => ({ name: `${s.firstname} ${s.lastname}`, value: s.id.toString() }))
                    : []
        }
    ]

    const selectOptionsSectionGroups: SelectOptionsDeprecated = [
        {
            options:
                daycare?.sectionsGroups?.map((sg) => ({
                    name: sg.name.valueOf(),
                    value: sg.id.toString()
                })) || []
        }
    ]

    const WORK_TYPES: WorkType[] = [WorkType.inSection, WorkType.holiday, WorkType.training, WorkType.notInSection]
    const selectOptionsWorkType: SelectOptionsDeprecated = [
        {
            options: WORK_TYPES.map(
                (wt: WorkType): SelectOptionDeprecated => ({ value: wt.toString(), name: t(wt.toString()) })
            )
        }
    ]

    const selfOverlaps: boolean =
        startHour.length === 5 &&
        endHour.length === 5 &&
        !autocompleteStaff[0].options.find((o: SelectOptionDeprecated) => o.value === staff)

    const daysHoursValid: boolean =
        !daysMode ||
        selectedDays.every((day: string) => {
            const times: { start: string; end: string } = dayTimes[day] || { start: startHour, end: endHour }
            return times.start.length === 5 && times.end.length === 5 && times.start < times.end
        })

    function isPauseValid(
        start: string,
        end: string,
        pause: { hasPause: boolean; start: string; end: string }
    ): boolean {
        return (
            !pause.hasPause ||
            (pause.start.length === 5 &&
                pause.end.length === 5 &&
                start <= pause.start &&
                pause.start < pause.end &&
                pause.end <= end)
        )
    }

    const pauseValid: boolean = daysMode
        ? selectedDays.every((day: string) => {
              const times: { start: string; end: string } = dayTimes[day] || { start: startHour, end: endHour }
              const pause: { hasPause: boolean; start: string; end: string } = dayPause[day] || {
                  hasPause,
                  start: pauseStart,
                  end: pauseEnd
              }
              return isPauseValid(times.start, times.end, pause)
          })
        : isPauseValid(startHour, endHour, { hasPause, start: pauseStart, end: pauseEnd })

    const validCreate: boolean =
        (daysMode
            ? daysHoursValid && selectedDays.length > 0
            : startHour.length === 5 && endHour.length === 5 && startHour < endHour) &&
        (daysMode || (moment(startHour, 'HH:mm').isValid() && moment(endHour, 'HH:mm').isValid())) &&
        pauseValid &&
        !noClosingDay.length &&
        (workType === WorkType.inSection
            ? !!selectOptionsSectionGroups[0].options.find((o: SelectOptionDeprecated) => o.value === sectionGroup)
            : true) &&
        !selfOverlaps &&
        !!autocompleteStaff[0].options.find((o: SelectOptionDeprecated) => o.value === staff) &&
        (chooseDate ? dateList.length > 0 : true)

    const validBulkEdit: boolean =
        (daysMode
            ? daysHoursValid && selectedDays.length > 0
            : startHour.length === 5 && endHour.length === 5 && startHour < endHour) &&
        pauseValid &&
        (workType === WorkType.inSection
            ? !!selectOptionsSectionGroups[0].options.find((o: SelectOptionDeprecated) => o.value === sectionGroup)
            : true) &&
        (chooseDate ? dateList.length > 0 : true) &&
        (selectPeriod ? !!startDate && !!endDate : true)

    const currentWorkValues: WorkSnapshot = {
        staff,
        sectionGroup,
        startHour,
        endHour,
        internalNoteValue,
        externalNoteValue,
        workType,
        preparationTasksBefore: sortedIds(hasPreparation ? preparationTasksBefore : []),
        preparationTasksAfter: sortedIds(hasPreparation ? preparationTasksAfter : []),
        pauseStart: hasPause ? pauseStart : '',
        pauseEnd: hasPause ? pauseEnd : ''
    }

    const isWorkUnchanged: boolean = !!initialWorkValues && isSameSnapshot(currentWorkValues, initialWorkValues)

    const [createWork] = useMutation(CREATE_MULTIPLE_WORK, {
        onCompleted(data) {
            const skippedCount: number = data?.createMultipleWork?.skippedDates?.length ?? 0
            if (skippedCount > 0) {
                showNotificationError(t('workCreatedWithSkips', { count: skippedCount }))
            } else {
                showNotificationSuccess(t(isBulkEditMode ? 'workUpdated' : 'workCreated'))
            }
            refetch()
            handleClose()
            if (isBulkEditMode && closeParentDialog) closeParentDialog()
        },
        onError() {
            showNotificationError(t(isBulkEditMode ? 'errorUpdatedWork' : 'errorCreatingWork'))
        }
    })

    const [updateWork] = useMutation(UPDATE_INTERNAL_WORK, {
        onCompleted() {
            showNotificationSuccess(t('workUpdated'))
            refetch()
            handleClose()
            if (closeParentDialog) closeParentDialog()
        },
        onError() {
            showNotificationError(t('errorUpdatedWork'))
        }
    })

    function submitWork(replaceExisting: boolean): void {
        const staffPreparationTaskIdsBefore: number[] = hasPreparation ? preparationTasksBefore.map(Number) : []
        const staffPreparationTaskIdsAfter: number[] = hasPreparation ? preparationTasksAfter.map(Number) : []

        if (chooseDate) {
            createWork({
                variables: {
                    daycareId: daycare?.id,
                    staff: Number(staff),
                    type: workType,
                    sectionGroupId: sectionGroup && workType === WorkType.inSection ? Number(sectionGroup) : undefined,
                    internalNote: internalNoteValue || undefined,
                    externalNote: externalNoteValue || undefined,
                    dateList,
                    start: startHour,
                    end: endHour,
                    staffPreparationTaskIdsBefore,
                    staffPreparationTaskIdsAfter,
                    pauseStart: hasPause ? pauseStart : undefined,
                    pauseEnd: hasPause ? pauseEnd : undefined,
                    replaceExisting
                }
            })
        } else if (daysMode && selectedDays.length > 0) {
            const resolvedDayTimes: {
                day: number
                start: string
                end: string
                staffPreparationTaskIdsBefore: number[]
                staffPreparationTaskIdsAfter: number[]
                pauseStart?: string
                pauseEnd?: string
            }[] = selectedDays.map(
                (
                    day: string
                ): {
                    day: number
                    start: string
                    end: string
                    staffPreparationTaskIdsBefore: number[]
                    staffPreparationTaskIdsAfter: number[]
                    pauseStart?: string
                    pauseEnd?: string
                } => {
                    const times: { start: string; end: string } = dayTimes[day] || { start: startHour, end: endHour }
                    const tasks: { before: string[]; after: string[] } = dayPreparationTasks[day] || {
                        before: preparationTasksBefore,
                        after: preparationTasksAfter
                    }
                    const pause: { hasPause: boolean; start: string; end: string } = dayPause[day] || {
                        hasPause,
                        start: pauseStart,
                        end: pauseEnd
                    }
                    return {
                        day: Number(day),
                        start: times.start,
                        end: times.end,
                        staffPreparationTaskIdsBefore: hasPreparation ? tasks.before.map(Number) : [],
                        staffPreparationTaskIdsAfter: hasPreparation ? tasks.after.map(Number) : [],
                        pauseStart: pause.hasPause ? pause.start : undefined,
                        pauseEnd: pause.hasPause ? pause.end : undefined
                    }
                }
            )
            createWork({
                variables: {
                    daycareId: daycare?.id,
                    staff: Number(staff),
                    type: workType,
                    sectionGroupId: sectionGroup && workType === WorkType.inSection ? Number(sectionGroup) : undefined,
                    internalNote: internalNoteValue || undefined,
                    externalNote: externalNoteValue || undefined,
                    from: startDate,
                    to: endDate,
                    days: selectedDays.map(Number),
                    dayTimes: resolvedDayTimes,
                    replaceExisting,
                    ...(weekPattern.chooseWeekPattern ? weekPattern.getMutationVariables(startDate) : {})
                }
            })
        } else if (selectPeriod || startDate !== endDate) {
            createWork({
                variables: {
                    daycareId: daycare?.id,
                    staff: Number(staff),
                    type: workType,
                    sectionGroupId: sectionGroup && workType === WorkType.inSection ? Number(sectionGroup) : undefined,
                    internalNote: internalNoteValue || undefined,
                    externalNote: externalNoteValue || undefined,
                    from: startDate,
                    to: endDate,
                    start: startHour,
                    end: endHour,
                    staffPreparationTaskIdsBefore,
                    staffPreparationTaskIdsAfter,
                    pauseStart: hasPause ? pauseStart : undefined,
                    pauseEnd: hasPause ? pauseEnd : undefined,
                    replaceExisting
                }
            })
        } else {
            createWork({
                variables: {
                    daycareId: daycare?.id,
                    staff: Number(staff),
                    type: workType,
                    sectionGroupId: sectionGroup && workType === WorkType.inSection ? Number(sectionGroup) : undefined,
                    internalNote: internalNoteValue || undefined,
                    externalNote: externalNoteValue || undefined,
                    dateList: [startDate],
                    start: startHour,
                    end: endHour,
                    staffPreparationTaskIdsBefore,
                    staffPreparationTaskIdsAfter,
                    pauseStart: hasPause ? pauseStart : undefined,
                    pauseEnd: hasPause ? pauseEnd : undefined,
                    replaceExisting
                }
            })
        }
    }

    function handleCreate(): void {
        submitWork(false)
    }

    function handleBulkUpdate(): void {
        submitWork(true)
    }

    function handleUpdate(): void {
        updateWork({
            variables: {
                workId: work?.id,
                daycareId: daycare?.id,
                work: {
                    date: startDate,
                    start: startHour,
                    end: endHour,
                    staff: Number(staff),
                    sectionGroupId: sectionGroup && workType === WorkType.inSection ? Number(sectionGroup) : undefined,
                    type: workType,
                    internalNote: internalNoteValue,
                    externalNote: externalNoteValue,
                    staffPreparationTaskIdsBefore: hasPreparation ? preparationTasksBefore.map(Number) : [],
                    staffPreparationTaskIdsAfter: hasPreparation ? preparationTasksAfter.map(Number) : [],
                    pauseStart: hasPause ? pauseStart : undefined,
                    pauseEnd: hasPause ? pauseEnd : undefined
                }
            }
        })
    }

    const { dialog: SelectMultipleDateDialog, openDialog: openMultipleDateDialog } = useDialog({
        Component: SelectMultipleDateDialogComponent,
        props: () => ({
            daycare: daycare,
            dateList,
            setDateList,
            startDate,
            closingDays: []
        }),
        dialogTitle: false
    })

    return (
        <>
            <Dialog
                open={open}
                handleClose={handleClose}
                className={`${styles.WorkDialog} ${activePreviewDay ? styles.WithPreview : ''}`}
            >
                <div className={styles.DialogLayout}>
                    <div className={styles.DialogContent}>
                        <DialogHeader title={`${isCreate ? t('add') : t('edit')} - ${t(workType)}`} />
                        <DialogBody className={styles.DialogBody}>
                            <div className={styles.InputContainer}>
                                <div className={styles.Input}>
                                    {chooseDate ? (
                                        <Autocomplete
                                            multiple
                                            options={dateList}
                                            limitTags={3}
                                            value={dateList}
                                            onChange={(_event: SyntheticEvent, newValue: string[]) =>
                                                setDateList(newValue)
                                            }
                                            renderTags={(value: string[], getTagProps: AutocompleteRenderGetTagProps) =>
                                                value.map((option: string, index: number) => (
                                                    <div key={index}>
                                                        <Chip
                                                            label={option}
                                                            {...getTagProps({ index })}
                                                            onDelete={() => handleDateRemove(option)}
                                                        />
                                                    </div>
                                                ))
                                            }
                                            renderInput={(params: AutocompleteRenderInputParams) => (
                                                <TextField
                                                    {...params}
                                                    variant='outlined'
                                                    rows={1}
                                                    multiline={true}
                                                    label={t('dateList')}
                                                />
                                            )}
                                            className={styles.Autocomplete}
                                            onOpen={openMultipleDateDialog}
                                        />
                                    ) : isCreate || selectPeriod ? (
                                        <>
                                            <DatePickerComponent
                                                value={startDate}
                                                readOnly={false}
                                                label={t('fromDate')}
                                                setValue={setStartDate}
                                                disablePast={false}
                                                maxDate={endDate}
                                                className={styles.UserInput}
                                            />
                                            <DatePickerComponent
                                                value={endDate}
                                                readOnly={false}
                                                label={t('toDate')}
                                                setValue={setEndDate}
                                                disablePast={false}
                                                minDate={startDate}
                                                className={styles.UserInput}
                                            />
                                        </>
                                    ) : (
                                        <DatePickerComponent
                                            value={startDate}
                                            readOnly={dateReadonly}
                                            label={t('date')}
                                            setValue={setStartDate}
                                            disablePast={false}
                                            className={styles.UserInput}
                                        />
                                    )}
                                </div>
                            </div>

                            {isCreate && !chooseDays && !weekPattern.chooseWeekPattern && (
                                <div className={styles.CheckboxContainer}>
                                    <Checkbox
                                        text={t('chooseDate')}
                                        checked={chooseDate}
                                        onChange={handleCheckChooseDate}
                                        className={styles.Checkbox}
                                    />
                                </div>
                            )}

                            {!isCreate && (
                                <div className={styles.CheckboxContainer}>
                                    <Checkbox
                                        text={t('selectOtherDates')}
                                        checked={chooseDate}
                                        onChange={handleCheckChooseDate}
                                        className={styles.Checkbox}
                                    />
                                    <Checkbox
                                        text={t('selectPeriod')}
                                        checked={selectPeriod}
                                        onChange={handleCheckSelectPeriod}
                                        className={styles.Checkbox}
                                    />
                                </div>
                            )}

                            {!isCreate && selectPeriod && (
                                <>
                                    <div className={styles.CheckboxContainer}>
                                        <Checkbox
                                            text={t('chooseRecurrence')}
                                            checked={chooseDays}
                                            onChange={handleCheckChooseDays}
                                            className={styles.Checkbox}
                                        />
                                    </div>
                                    {chooseDays && (
                                        <div className={styles.InputContainer}>
                                            <div className={styles.Input}>
                                                <SelectMultiple
                                                    selectOptions={selectDays}
                                                    label={t('days')}
                                                    selectValues={selectedDays}
                                                    setSelectValues={setSelectedDays}
                                                    className={styles.UserInput}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {daysMode && selectedDays.length > 0 ? (
                                <div className={styles.DayTimesContainer}>
                                    {[...selectedDays]
                                        .sort((a: string, b: string): number => Number(a) - Number(b))
                                        .map((day: string): JSX.Element => {
                                            const times: { start: string; end: string } = dayTimes[day] || {
                                                start: startHour,
                                                end: endHour
                                            }

                                            return (
                                                <DayTimeRow
                                                    key={day}
                                                    onPreview={() =>
                                                        setPreviewDay((current) => (current === day ? null : day))
                                                    }
                                                    previewActive={activePreviewDay === day}
                                                    dayLabel={getDayLabel(day)}
                                                    start={times.start}
                                                    end={times.end}
                                                    setStart={(v: string) => updateDayTime(day, 'start', v)}
                                                    setEnd={(v: string) => updateDayTime(day, 'end', v)}
                                                    minHour={minHour}
                                                    minMinute={minMinute}
                                                    maxHour={maxHour}
                                                    maxMinute={maxMinute}
                                                    minuteToDisable={range}
                                                    workType={workType}
                                                    hasPause={dayPause[day]?.hasPause || false}
                                                    setHasPause={(v) => updateDayPause(day, 'hasPause', v)}
                                                    pauseStart={dayPause[day]?.start || ''}
                                                    setPauseStart={(v) => updateDayPause(day, 'start', v)}
                                                    pauseEnd={dayPause[day]?.end || ''}
                                                    setPauseEnd={(v) => updateDayPause(day, 'end', v)}
                                                    showPreparationFields={showPreparationFields}
                                                    daycare={daycare}
                                                    preparationBefore={dayPreparationTasks[day]?.before || []}
                                                    setPreparationBefore={(v) =>
                                                        updateDayPreparationTasks(day, 'before', v)
                                                    }
                                                    preparationAfter={dayPreparationTasks[day]?.after || []}
                                                    setPreparationAfter={(v) =>
                                                        updateDayPreparationTasks(day, 'after', v)
                                                    }
                                                    canUpdatePreparationTasks={canUpdatePreparationTasks}
                                                />
                                            )
                                        })}
                                </div>
                            ) : (
                                <div className={styles.DayTimesContainer}>
                                    <DayTimeRow
                                        onPreview={() =>
                                            setPreviewDay((current) => (current === 'single' ? null : 'single'))
                                        }
                                        previewActive={activePreviewDay === 'single'}
                                        start={startHour}
                                        end={endHour}
                                        setStart={setStartHour}
                                        setEnd={setEndHour}
                                        minHour={minHour}
                                        minMinute={minMinute}
                                        maxHour={maxHour}
                                        maxMinute={maxMinute}
                                        minuteToDisable={range}
                                        workType={workType}
                                        hasPause={hasPause}
                                        setHasPause={setHasPause}
                                        pauseStart={pauseStart}
                                        setPauseStart={setPauseStart}
                                        pauseEnd={pauseEnd}
                                        setPauseEnd={setPauseEnd}
                                        showPreparationFields={showPreparationFields}
                                        daycare={daycare}
                                        preparationBefore={preparationTasksBefore}
                                        setPreparationBefore={setPreparationTasksBefore}
                                        preparationAfter={preparationTasksAfter}
                                        setPreparationAfter={setPreparationTasksAfter}
                                        canUpdatePreparationTasks={canUpdatePreparationTasks}
                                    />
                                </div>
                            )}
                            {workType !== WorkType.holiday && (
                                <div className={styles.CheckboxContainer}>
                                    <Checkbox
                                        text={t('addPreparationTime')}
                                        checked={hasPreparation}
                                        onChange={handleCheckPreparation}
                                        className={styles.Checkbox}
                                    />
                                </div>
                            )}

                            {isCreate && !chooseDate && (
                                <>
                                    <div className={styles.CheckboxContainer}>
                                        <Checkbox
                                            text={t('chooseRecurrence')}
                                            checked={chooseDays}
                                            onChange={handleCheckChooseDays}
                                            className={styles.Checkbox}
                                        />
                                    </div>
                                    {chooseDays && (
                                        <div className={styles.InputContainer}>
                                            <div className={styles.Input}>
                                                <SelectMultiple
                                                    selectOptions={selectDays}
                                                    label={t('days')}
                                                    selectValues={selectedDays}
                                                    setSelectValues={setSelectedDays}
                                                    className={styles.UserInput}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    <div className={styles.CheckboxContainer}>
                                        <Checkbox
                                            text={t('chooseWeekPattern')}
                                            checked={weekPattern.chooseWeekPattern}
                                            onChange={handleCheckChooseWeekPattern}
                                            className={styles.Checkbox}
                                        />
                                    </div>
                                    {weekPattern.chooseWeekPattern && (
                                        <WeekPatternSection
                                            {...weekPattern}
                                            selectedDays={selectedDays}
                                            setSelectedDays={setSelectedDays}
                                            selectDays={selectDays}
                                        />
                                    )}
                                </>
                            )}

                            <div className={styles.InputContainer}>
                                <div className={styles.Input}>
                                    <SelectUnique
                                        selectOptions={selectOptionsWorkType}
                                        label={t('type')}
                                        selectValue={workType}
                                        setSelectValue={setWorkType}
                                        formClassName={styles.UserInput}
                                    />
                                </div>
                                {workType === WorkType.inSection && (
                                    <div className={styles.Input}>
                                        <SelectUnique
                                            selectOptions={selectOptionsSectionGroups}
                                            label={t('management')}
                                            selectValue={sectionGroup}
                                            setSelectValue={setSectionGroup}
                                            formClassName={styles.UserInput}
                                        />
                                    </div>
                                )}
                            </div>

                            <div
                                className={styles.MoreInfoContainer}
                                onClick={() => setShowMoreOptions(!showMoreOptions)}
                            >
                                <Title text={t('moreOptions')} className={styles.Title} />
                                {showMoreOptions ? (
                                    <KeyboardArrowUp className={styles.Icon} />
                                ) : (
                                    <KeyboardArrowDown className={styles.Icon} />
                                )}
                            </div>
                            {showMoreOptions && (
                                <div className={styles.InputContainer}>
                                    <div className={styles.Input}>
                                        <SelectUnique
                                            selectOptions={autocompleteStaff}
                                            label={t('staff')}
                                            selectValue={staff}
                                            setSelectValue={setStaff}
                                            formClassName={styles.UserInput}
                                        />
                                    </div>
                                    <div className={styles.TextAreaInput}>
                                        <p className={styles.TextAreaTitle}>{t('internalNote')}</p>
                                        <TextArea
                                            placeholder={t('internalNote')}
                                            className={styles.TextArea}
                                            value={internalNoteValue}
                                            onChange={setInternalNoteValue}
                                            maxCount={100}
                                        />
                                    </div>
                                    <div className={styles.TextAreaInput}>
                                        <p className={styles.TextAreaTitle}>{t('externalNote')}</p>
                                        <TextArea
                                            placeholder={t('externalNote')}
                                            className={styles.TextArea}
                                            value={externalNoteValue}
                                            onChange={setExternalNoteValue}
                                            maxCount={100}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className={styles.ButtonContainer}>
                                <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                                {isCreate && (
                                    <ButtonTextIcon
                                        type={'add'}
                                        onClick={handleCreate}
                                        className={styles.Button}
                                        disabled={!validCreate}
                                    />
                                )}
                                {!isCreate && (
                                    <ButtonTextIcon
                                        type={'update'}
                                        onClick={isBulkEditMode ? handleBulkUpdate : handleUpdate}
                                        className={styles.Button}
                                        disabled={isBulkEditMode ? !validBulkEdit : isWorkUnchanged}
                                    />
                                )}
                            </div>
                        </DialogBody>
                    </div>
                    {activePreviewDay && (
                        <WorkPreview
                            label={daysMode ? getDayLabel(activePreviewDay) : startDate}
                            start={previewTimes.start}
                            end={previewTimes.end}
                            opening={
                                daycare ? getDaycareOpening(daycare, previewDayOfWeek) || previewTimes.start : '08:00'
                            }
                            closing={
                                daycare ? getDaycareClosing(daycare, previewDayOfWeek) || previewTimes.end : '18:00'
                            }
                            workType={workType}
                            hasPause={workType !== WorkType.holiday && !!previewPause?.hasPause}
                            pauseStart={previewPause?.start || ''}
                            pauseEnd={previewPause?.end || ''}
                            preparationBefore={showPreparationFields ? previewPreparation?.before || [] : []}
                            preparationAfter={showPreparationFields ? previewPreparation?.after || [] : []}
                            daycareId={daycare?.id}
                        />
                    )}
                </div>
            </Dialog>

            {SelectMultipleDateDialog}
        </>
    )
}

export default EditOrCreateWorkDialog

type WorkSnapshot = {
    staff: string
    sectionGroup: string
    startHour: string
    endHour: string
    internalNoteValue: string
    externalNoteValue: string
    workType: string
    preparationTasksBefore: string
    preparationTasksAfter: string
    pauseStart: string
    pauseEnd: string
}

function sortedIds(ids: string[]): string {
    return [...ids].sort((a: string, b: string): number => Number(a) - Number(b)).join(',')
}

function getWorkSnapshot(work: Work): WorkSnapshot {
    return {
        staff: work.staff.toString(),
        sectionGroup: work.sectionGroupId?.toString() || '',
        startHour: moment(work.start, 'HH:mm').format('HH:mm'),
        endHour: moment(work.end, 'HH:mm').format('HH:mm'),
        internalNoteValue: work.internalNote || '',
        externalNoteValue: work.externalNote || '',
        workType: work.type.toString(),
        preparationTasksBefore: sortedIds(work.preparationTasksBefore?.map((t) => t.id.toString()) || []),
        preparationTasksAfter: sortedIds(work.preparationTasksAfter?.map((t) => t.id.toString()) || []),
        pauseStart: '',
        pauseEnd: ''
    }
}

function isSameSnapshot(a: WorkSnapshot, b: WorkSnapshot): boolean {
    return (Object.keys(a) as Array<keyof WorkSnapshot>).every((key: keyof WorkSnapshot) => a[key] === b[key])
}
